import React, { useState } from 'react';
import { ElevatedButton, TextButton } from '@producer/ui-kit';

import { PlanCardCheckOn } from '../../../components/icons';
import { Plan } from '../../../routes/settings/plans-settings.route';

import styles from './plan-card.module.scss';
type CardProps = Omit<Plan, 'productId' | 'price'> & {
  isSelfServing: boolean;
  onSelect(priceId: string): void;
  handleDowngrade: () => void;
  handleUpgrade: () => void;
};

export const PlanCard: React.FC<CardProps> = ({
  name,
  description,
  label,
  features,
  disabled,
  formattedPrice,
  priceId,
  status,
  interval,
  isSelfServing,
  onSelect,
  handleDowngrade,
  handleUpgrade,
}) => {
  const renderButton = () => {
    const [isLoading, setIsLoading] = useState(false);

    const cycleName = interval === 'month' ? 'monthly' : 'yearly';

    if (disabled) {
      return <ElevatedButton text="Get in touch" onClick={handleUpgrade} isFullWidth />;
    }

    const handleOnSelect = async () => {
      setIsLoading(true);
      await onSelect(priceId);
      setTimeout(() => setIsLoading(false), 500);
    };

    switch (status) {
      case 'currentPlan':
        return <ElevatedButton text="Current plan" variant="secondary" isFullWidth disabled />;

      case 'changeInterval':
        return (
          isSelfServing && (
            <ElevatedButton
              text={`Change to ${cycleName}`}
              variant="default"
              onClick={handleDowngrade}
              isFullWidth
            />
          )
        );

      case 'upgrade':
        return (
          isSelfServing && (
            <ElevatedButton
              text="Start free trial"
              variant="primary"
              onClick={handleOnSelect}
              isFullWidth
              isLoading={isLoading}
              disabled={isLoading}
            />
          )
        );

      case 'downgrade':
        return (
          isSelfServing && (
            <TextButton text="Downgrade plan" onClick={handleDowngrade} isFullWidth />
          )
        );

      default:
        return (
          isSelfServing && (
            <ElevatedButton text="Get in touch" onClick={handleUpgrade} isFullWidth />
          )
        );
    }
  };

  return (
    <div className={styles['container']} data-current={status === 'currentPlan'}>
      <div className={styles['plan-container']}>
        <div className={styles['plan-details']}>
          <label className={styles['label']}>{label}</label>
          <h2 className={styles['title']}>
            {name.split(' ')[0]}
            <br />
            {name.split(' ')[1]}
          </h2>
          <p className={styles['description']}>{description}</p>
        </div>
      </div>

      {formattedPrice ? (
        <div className={styles['price']}>
          <span>{formattedPrice}</span>
          <span>per user/month</span>
        </div>
      ) : (
        <h6 className={styles['custom-price']}>
          <b>from $1'000</b> /month
        </h6>
      )}

      {renderButton()}

      <div className={styles['plan-information']}>
        {features?.map((info) => {
          return (
            <div className={styles['information-card']} key={info}>
              <PlanCardCheckOn />
              <p className={styles['information-text']}>{info}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};
